import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Tag, Users } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";

export default function DraggableCard({ card, onClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.id,
  });
  const [assignedUsersData, setAssignedUsersData] = useState([]);

  useEffect(() => {
    if (card.assignedUsers && card.assignedUsers.length > 0) {
      fetchAssignedUsers();
    }
  }, [card.assignedUsers]);

  const fetchAssignedUsers = async () => {
    try {
      // Fetch user details for assigned users
      const userPromises = card.assignedUsers.slice(0, 3).map(userId => 
        axios.get(`/users`)
          .then(res => res.data.find(u => u.id === userId))
          .catch(() => null)
      );
      const users = await Promise.all(userPromises);
      setAssignedUsersData(users.filter(Boolean));
    } catch (error) {
      console.error("Error fetching assigned users:", error);
    }
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      data-testid={`card-${card.id}`}
      className="cursor-move hover:shadow-lg transition-all duration-200 border-l-4 border-l-blue-500 bg-white"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <h4 className="font-medium text-gray-900 mb-2" data-testid={`card-title-${card.id}`}>{card.title}</h4>
        {card.description && (
          <p className="text-sm text-gray-600 mb-2 line-clamp-2">{card.description}</p>
        )}
        <div className="flex flex-wrap gap-2 mt-2">
          {card.labels && card.labels.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {card.labels.map((label, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                  style={{ backgroundColor: label.color, color: '#fff' }}
                >
                  {label.text}
                </span>
              ))}
            </div>
          )}
          {card.dueDate && (
            <div className="flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
              <Calendar className="w-3 h-3" />
              {new Date(card.dueDate).toLocaleDateString('de-DE')}
            </div>
          )}
        </div>
        {/* Assigned Users */}
        {card.assignedUsers && card.assignedUsers.length > 0 && (
          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-100">
            <Users className="w-3 h-3 text-gray-500" />
            <div className="flex -space-x-2">
              {assignedUsersData.map((user, idx) => (
                <div
                  key={user.id}
                  className="relative"
                  title={user.name || user.username}
                >
                  {user.avatarUrl ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${user.avatarUrl}`}
                      alt={user.name || user.username}
                      className="w-6 h-6 rounded-full border-2 border-white object-cover"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full border-2 border-white bg-blue-100 flex items-center justify-center">
                      <span className="text-xs font-medium text-blue-600">
                        {(user.name || user.username).charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
              ))}
              {card.assignedUsers.length > 3 && (
                <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center">
                  <span className="text-xs font-medium text-gray-600">
                    +{card.assignedUsers.length - 3}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
